import {getTranslatedReviews} from '../api/translateReviews';
import ReviewsActions from '../actions/reviewsActions';
import ListingActions from '../actions/listingActions';
import EdapActions from '../actions/edapActions';
import {getLanguage} from '../utils/localeUtils';

function getReviewsRelatedDataFromStore(store) {
    const state = store.getState();
    const {reviewsReducer, systemReducer} = state;
    const reviewsData = reviewsReducer.toJS();
    const {pageIndex, reviewCount} = reviewsData;
    const {reviewPageSize, locale, isMobile} = systemReducer;
    const pageStartIndex = (pageIndex - 1) * reviewPageSize;
    const pageEndIndex =
        pageIndex * reviewPageSize <= reviewCount ? pageIndex * reviewPageSize : reviewCount;

    return {
        isMobile,
        locale,
        pageIndex,
        pageStartIndex,
        pageEndIndex,
        reviewsData
    };
}

function isNonSiteLangReviewPresent(reviews) {
    const isAnyReviewInNonSiteLang = reviews.some((review) => {
        return review.isNonSiteLanguageReview;
    });
    return isAnyReviewInNonSiteLang;
}

function dispatchTranslateReviewsErrorEvents(store, error) {
    store.dispatch(ReviewsActions.scrollTop(false));
    store.dispatch(ReviewsActions.translateReviewsFailure(error));
}

async function fetchTranslatedReviews(store, reviewList) {
    try {
        const {locale} = getReviewsRelatedDataFromStore(store);
        const language = getLanguage(locale);
        const translationResponse = await getTranslatedReviews({
            reviews: reviewList,
            language
        });

        store.dispatch(ReviewsActions.translateReviewsSuccess(translationResponse.body));

        return true;
    } catch (error) {
        dispatchTranslateReviewsErrorEvents(store, error);

        return false;
    }
}

// Two ways to trigger translations
// 1) pagination/scroll, in which case we fetch new reviews anyway and then translate them if needed
// 2) "translate" on click, in which case we either translate the page (desktop) or all visible reviews (mobile)
async function fetchReviewsSuccess(store, action = {payload: {}}) {
    const {reviews: newReviews} = action.payload;
    const {reviewsData} = getReviewsRelatedDataFromStore(store);
    const {translatedReviewsByUuid} = reviewsData;

    // Call for translations if needed
    if (newReviews && reviewsData.isTranslationToggleOn) {
        const isTranslationRequired = isNonSiteLangReviewPresent(newReviews);
        const reviewsNeedingTranslation = [];
        newReviews.forEach((newReview) => {
            if (!translatedReviewsByUuid[newReview.uuid]) {
                reviewsNeedingTranslation.push(newReview);
            }
        });

        if (isTranslationRequired && reviewsNeedingTranslation.length) {
            store.dispatch(ReviewsActions.fetchTranslatedReviewsStarted());
            await fetchTranslatedReviews(store, reviewsNeedingTranslation);
        }
    }
}

async function translateReviews(store) {
    const {isMobile, reviewsData, pageEndIndex, pageStartIndex} = getReviewsRelatedDataFromStore(
        store
    );

    const {reviews, translatedReviewsByUuid} = reviewsData;
    const reviewsNeedingTranslation = [];
    let successFlag = true;
    let visibleReviews;

    // get visible reviews for desktop or mobile
    if (isMobile) {
        visibleReviews = reviews;
    } else {
        visibleReviews = reviews.slice(pageStartIndex, pageEndIndex);
    }

    // get reviews that do not already have translations in store
    visibleReviews.forEach((review) => {
        if (!translatedReviewsByUuid[review.uuid]) {
            reviewsNeedingTranslation.push(review);
        }
    });

    // translate those reviews
    if (reviewsNeedingTranslation.length) {
        store.dispatch(ReviewsActions.fetchTranslatedReviewsStarted());
        successFlag = await fetchTranslatedReviews(store, reviewsNeedingTranslation);
    }

    // set autotranslation on, so subsequent pages will show translations
    if (successFlag) {
        store.dispatch(ReviewsActions.updateIsTranslationToggleOn(true));
    }
}

function showOriginalReviews(store) {
    store.dispatch(ReviewsActions.updateIsTranslationToggleOn(false));
}

function viewAllReviews(store, action = {payload: {}}) {
    const {uuid} = action.payload;
    const reviewId = typeof uuid === 'string' && uuid.length > 0 ? uuid : '';

    store.dispatch(ReviewsActions.expandReviewClicked(reviewId));
    store.dispatch(ListingActions.setReviewsModalOpen(true));
    store.dispatch(
        EdapActions.triggerEdapEvent('generic.modal', {
            modalname: 'reviews',
            modalaction: 'open'
        })
    );
}

export default {
    dispatchTranslateReviewsErrorEvents,
    fetchReviewsSuccess,
    fetchTranslatedReviews,
    isNonSiteLangReviewPresent,
    showOriginalReviews,
    translateReviews,
    viewAllReviews
};
export {
    dispatchTranslateReviewsErrorEvents,
    fetchReviewsSuccess,
    fetchTranslatedReviews,
    isNonSiteLangReviewPresent,
    showOriginalReviews,
    translateReviews,
    viewAllReviews
};
